// liveComplete.js — what to suggest at the cursor in the live console.
// -----------------------------------------------
// Pure: given the line up to the cursor and what the console knows (the
// cues, their parameters and values, the names made so far, the signal
// channels, the phrases in the project's stash), say what is being typed
// and what it could become. The console shows the best one as ghost text
// (Tab takes it) and the rest on demand.

import { easings } from "../control/easing.js";
import { SCALES } from "../cues/audio/scales.js";
import { offeredIn } from "../parser/cueNames.js";

/** The console's own lines, which are no cues (oscillaLive.js keeps them out of scenes). */
export const CONSOLE_LINES = ["snapshot", "versions", "room", "clock", "downbeat", "latency", "map", "folder"];

/** The cues a performer types at the top of a line (cueNames.js), and the console's own. */
const LIVE_CUES = [...offeredIn("c"), ...CONSOLE_LINES];

/** Parameters of the console's own cues, which the DSL editor does not list. */
export const LIVE_PARAMS = {
  shape: [
    { key: "uid" }, { key: "sides" }, { key: "size" }, { key: "at" }, { key: "n" },
    { key: "fill" }, { key: "stroke" }, { key: "width" }, { key: "opacity" },
    { key: "m" }, { key: "n1" }, { key: "n2" }, { key: "n3" }, { key: "morph" },
    { key: "blend", options: ["normal", "difference", "exclusion", "screen", "multiply", "overlay", "lighten", "darken"] },
    { key: "cell", options: ["free", "any"] }, { key: "across" }, { key: "down" },
    { key: "remove", options: ["1", "all"] },
    { key: "drag", options: ["1", "0"] }, { key: "map", options: ["1", "points", "0"] }, { key: "unset" },
  ],
  viz: [
    { key: "size" }, { key: "at" }, { key: "cell", options: ["free", "any"] }, { key: "across" }, { key: "down" }, { key: "fill" }, { key: "stroke" }, { key: "width" },
    { key: "colour" }, { key: "border" }, { key: "opacity" }, { key: "blur" }, { key: "kaleido" }, { key: "turn" }, { key: "mirror", options: ["x", "y", "xy"] }, { key: "tile" },
    { key: "hue" }, { key: "invert" }, { key: "contrast" }, { key: "saturate" }, { key: "bright" }, { key: "pixel" }, { key: "warp" }, { key: "grain" }, { key: "split" }, { key: "fx", options: ["off"] },
    { key: "trail" }, { key: "spin" }, { key: "lag" }, { key: "cycle" },
    { key: "rows" }, { key: "span" }, { key: "step", options: ["0.25", "0.5", "1"] }, { key: "tween", options: ["1", "0.5", "0.25"] }, { key: "scroll", options: ["right", "left", "up", "down", "off"] }, { key: "glyphs", options: ["hex", "bin", "dec", "\"DATA\"", "off"] }, { key: "blend", options: ["normal", "difference", "exclusion", "screen", "multiply", "overlay", "lighten", "darken"] },
    { key: "view", options: ["wave", "polar", "phase", "xy", "spectrum", "ridge", "waterfall", "barcode"] },
    { key: "y" },
    { key: "palette", options: ["live", "grey", "primary", "pastel", "neon"] },
    { key: "drag", options: ["1", "0"] }, { key: "map", options: ["1", "0"] }, { key: "unset" }, { key: "mask" },
  ],
  hush: [{ key: "uid" }, { key: "rel" }],
  fx: [{ key: "reverb" }, { key: "delay" }, { key: "filter" }, { key: "sub" }, { key: "drive" }, { key: "fuzz" },
    { key: "fold" }, { key: "clip" }, { key: "ring" }, { key: "comb" }, { key: "crush" }, { key: "amp" }],
  solo: [{ key: "boost" }, { key: "fade" }, { key: "quant", options: ["1", "4"] }, { key: "trig" }],
  style: [{ key: "zoom" }, { key: "blur" }, { key: "fade" }, { key: "dur" }, { key: "gap" }, { key: "say" }, { key: "sayRate" }, { key: "span" }, { key: "flash" }, { key: "at" }],
  say: [
    { key: "bus" }, { key: "voice" }, { key: "speaker" }, { key: "rate" }, { key: "amp" }, { key: "pan" }, { key: "reverb" }, { key: "delay" },
    { key: "filter" }, { key: "waveform", options: ["none", "self"] }, { key: "trig" },
  ],
  use: [
    { key: "src" }, { key: "path" }, { key: "index" }, { key: "uid" }, { key: "size" }, { key: "at" },
    { key: "stroke" }, { key: "fill" }, { key: "color" }, { key: "width" }, { key: "opacity" },
    { key: "remove", options: ["1", "all"] },
  ],
  capture: [
    { key: "size" }, { key: "fps" }, { key: "format", options: ["mp4", "webm"] },
    { key: "wav", options: ["1", "0"] }, { key: "path" },
  ],
  tempo: [{ key: "bpm" }],
  speakers: [{ key: "azi" }, { key: "elev" }],
  vu: [{ key: "uid" }, { key: "size" }, { key: "at" }, { key: "floor" }, { key: "hold" }, { key: "stop", options: ["1"] }],
  resources: [{ key: "at", options: ["br", "bl", "tr", "tl"] }, { key: "stop", options: ["1"] }],
  screen: [
    { key: "kaleido" }, { key: "turn" }, { key: "mirror", options: ["x", "y", "xy", "0"] }, { key: "tile" },
    { key: "hue" }, { key: "invert" }, { key: "contrast" }, { key: "saturate" }, { key: "bright" },
    { key: "blur" }, { key: "pixel" }, { key: "warp" }, { key: "grain" }, { key: "split" },
    { key: "off", options: ["1"] }, { key: "clear", options: ["1"] },
  ],
  scatter: [{ key: "dur" }],
  adc: [{ key: "uid" }, { key: "ch", options: ["l", "r", "mix"] }, { key: "waveform", options: ["none"] }, { key: "off", options: ["1"] }],
  strobe: [{ key: "duty" }, { key: "color" }, { key: "invert", options: ["1"] }],
  scene: [{ key: "fade" }, { key: "via", options: ["blur", "warp", "pixel", "hue", "invert", "split", "black", "white", "wipe", "wipe-left"] }, { key: "curve", options: ["lin", "exp2", "exp3", "exp4", "log", "sqrt"] }, { key: "quant", options: ["0", "1", "4"] }],
  cam: [{ key: "uid" }, { key: "at" }, { key: "size" }, { key: "cell" }, { key: "flip", options: ["0", "1"] }, { key: "fit", options: ["cover", "contain"] },
    { key: "opacity" }, { key: "show", options: ["0", "1"] }, { key: "off", options: ["1"] },
    { key: "blur" }, { key: "warp" }, { key: "hue" }, { key: "pixel" }, { key: "invert" }, { key: "contrast" }, { key: "saturate" },
    { key: "bright" }, { key: "split" }, { key: "kaleido" }, { key: "turn" }, { key: "mirror", options: ["x", "y", "xy"] }, { key: "tile" }, { key: "fx", options: ["off"] }],
  grid: [{ key: "cols" }, { key: "rows" }, { key: "gap" }, { key: "show", options: ["1", "0"] }, { key: "turn" }, { key: "trig" }],
  level: [{ key: "to" }, { key: "dur" }, { key: "curve", options: ["lin", "exp2", "exp3", "exp4", "log", "sqrt"] }, { key: "uid" }],
  xfade: [{ key: "dur" }, { key: "curve", options: ["lin", "exp2", "exp3", "exp4", "log", "sqrt"] }],
  room: [{ key: "hear", options: ["mine", "all"] }],
};

/**
 * Values a parameter takes on any cue, where the cue's own list has none:
 * ease: on rotate, scale, trans, color, o2p ... (anime.js takes the names
 * without the ease prefix too; the full ones are offered).
 */
const BLENDS = ["normal", "difference", "exclusion", "screen", "multiply", "overlay", "lighten", "darken",
  "color-dodge", "color-burn", "hard-light", "soft-light", "hue", "saturation", "color", "luminosity"];
const ANY_CUE_VALUES = {
  ease: Object.keys(easings),
  loop: ["0", "1", "2", "4"],                  // 0: for ever; else how many times
  poly: ["0", "1", "2", "4", "8"],             // 0: no limit; 1: one at a time
  blend: BLENDS,
  waveform: ["self", "none"],
  vu: ["stereo", "mono"],                      // synth(..., vu:stereo): a pair in the meters
};

/**
 * Parameters a cue takes that its DSL editor list leaves out, with their
 * values: added to what ctx.params(cue) gives (paramsOf).
 */
const MORE_PARAMS = {
  synth: [{ key: "bus" }],
  audio: [{ key: "bus" }],
  text: [
    { key: "say" }, { key: "sayRate" }, { key: "from" }, { key: "span" },
    { key: "flash", options: ["white", "black", "invert"] },
    { key: "col" }, { key: "format" }, { key: "header", options: ["1", "0"] },
    { key: "blend", options: BLENDS },
  ],
};

/** A cue's parameters: the DSL editor's (or the console's own), and those it leaves out. */
function paramsOf(cue, ctx) {
  const base = ctx.params(cue) || [];
  const more = (MORE_PARAMS[cue] || []).filter(m => !base.some(b => b?.key === m.key));
  return more.length ? [...base, ...more] : base;
}

/** text()'s sources: a list, or a file in text/ (offered by name). */
const TEXT_SOURCES = new Set(["pick", "cycle", "shuffle", "scroll", "reveal", "ticker"]);

/**
 * Values to be asked for (ctx, liveLists.js): voices, the text/ files, a
 * .csv's columns -- or null where the parameter has none of these.
 */
function askedValues(c, ctx, before) {
  if (c.key === "voice") return ctx.voices?.() || [];
  if (c.key === "bus") return ["none", ...(ctx.buses?.() || [])];
  if (c.cue === "text" && c.key === "style") return ctx.styles?.() || [];   // style(agent, ...) presets
  if (c.key === "speaker") {
    // the line's voice: 0, or a speaker rolled each time from all it has
    const voice = /\bvoice\s*:\s*([\w-]+)/.exec(before)?.[1];
    const n = voice ? ctx.speakersOf?.(voice) || 1 : 1;
    return n > 1 ? ["0", `irand(0, ${n - 1})`] : [];
  }
  if (c.cue === "text" && c.key === "say") return ["1", ...(ctx.voices?.() || [])];
  if (c.cue === "text" && TEXT_SOURCES.has(c.key)) return ctx.textFiles?.() || [];
  if (c.cue === "text" && c.key === "col") {
    const file = /\b(?:pick|cycle|shuffle|scroll|reveal|ticker)\s*:\s*["']([^"']+\.csv)["']/.exec(before)?.[1];
    return file ? ctx.csvColumns?.(file) || [] : [];
  }
  return null;
}

/**
 * What is offered on opening a cue's bracket -- rotate( -- before anything is
 * typed: the parameters most lines use, in that order. A cue not listed
 * offers its parameters as the DSL editor lists them.
 */
const COMMON_PARAMS = {
  rotate:  ["dur", "dir", "axis", "ease", "loop", "pivot", "trig"],
  scale:   ["values", "dur", "ease", "loop", "interp", "pivot"],
  scaleXY: ["sx", "sy", "dur", "ease", "loop", "interp"],
  trans:   ["to", "dur", "ease", "mode", "loop", "hold"],
  color:   ["vals", "dur", "mode", "target", "loop"],
  fade:    ["from", "to", "dur", "mode", "target"],
  o2p:     ["path", "dur", "loop", "mode", "ease", "rotate"],
  synth:   ["wave", "freq", "amp", "dur", "env", "filter", "delay", "reverb", "azi"],
  audio:   ["src", "loop", "speed", "pan", "filter", "reverb", "azi"],
  shape:   ["sides", "size", "at", "fill", "stroke", "n"],
  use:     ["size", "at", "stroke", "fill", "path", "index"],
};

/** A template's keys and their defaults, in order: [["mix", "0.3"], ...]. */
const templatePairs = t => [...String(t).matchAll(/([A-Za-z_]\w*)\s*:\s*([^,}]+)/g)].map(m => [m[1], m[2].trim()]);

/** Values inside an object literal: filter:{type:...}. */
const OBJECT_VALUES = { type: ["lp", "hp", "bp", "notch"] };

/**
 * The object parameters, whole, with their defaults (synth docs): env:
 * offers {a:0.02, d:0, s:1, r:0.1} to take and change, rather than
 * remembering the keys. Inside the braces, the keys (OBJECT_KEYS).
 */
const OBJECT_TEMPLATES = {
  env: "{a:0.02, d:0, s:1, r:0.1}",
  envPerStep: "{a:0.005, d:0.03, s:0, r:0.01}",   // each step struck with it (synth.js)
  filter: "{type:lp, freq:1200, q:1}",
  delay: "{time:0.3, fb:0.3, mix:0.3}",
  reverb: "{mix:0.3, time:1.8, damp:1800, pre:0.015}",
  fuzz: "{starve:0.7, gain:30, bias:0.3, tone:2500}",
  sub: "{level:0.8, octave:1, tone:0.5}",
};
const OBJECT_KEYS = {
  env: ["a", "d", "s", "r"],
  envPerStep: ["a", "d", "s", "r"],
  filter: ["type", "freq", "q"],
  delay: ["time", "fb", "mix"],
  reverb: ["mix", "time", "damp", "pre"],
  fuzz: ["starve", "gain", "bias", "tone"],
  sub: ["level", "octave", "tone"],
};

/** Functions that may stand where a value goes. */
const VALUE_FUNCS = ["choose", "rand", "irand", "birand", "follow", "pseq", "prand", "pxrand", "pshuf", "pwhite", "pexprand",
  "pwrand", "pbrown", "pgauss", "plprand", "phprand", "pmeanrand", "pbeta", "pcauchy", "ppoisson", "peuclid",
  "sinOsc", "triOsc", "sawOsc", "pulseOsc", "lfNoise", "lfStep"];

/** sinOsc() and its family: their arguments, shown as a reminder while typing them. */
const SIGNATURES = {
  sinOsc: ["rate", "phase", "lo", "hi"], triOsc: ["rate", "phase", "lo", "hi"],
  sawOsc: ["rate", "phase", "lo", "hi"], pulseOsc: ["rate", "phase", "lo", "hi", "width"],
  lfNoise: ["rate", "lo", "hi"], lfStep: ["rate", "lo", "hi"],
};

/** Value functions whose arguments are values of the parameter they stand in: wave:choose(saw, ...). */
const PICKERS = new Set(["choose", "rand", "pseq", "prand", "pxrand", "pshuf", "pchoose", "Pseq", "Prand", "Pxrand", "Pshuf", "Pchoose"]);

/**
 * freq:Pwhite(40, 100) | scale(minor, root:A1): the pitch filters after |
 * (pitchSieve.js) -- what each takes, as a reminder, and what to offer in it.
 */
const PIPE_FILTERS = {
  scale: { sig: "name, root:, even", first: Object.keys(SCALES), keys: ["root", "even"] },
  notes: { sig: "[0, 3, 7] or [E, G, B], root:, even", first: ["[0, 3, 7]", "[E, G, B]"], keys: ["root", "even"] },
  edo: { sig: "steps to the octave, root:, even", first: ["12", "19", "22", "24", "31", "53"], keys: ["root", "even"] },
  harm: { sig: "fundamental (Hz or note), max:, even", first: ["27.5", "55", "A0", "E1"], keys: ["max", "even"] },
  sieve: { sig: "m@r + m@r * m@r, root:, even", first: ["3@0 + 4@1", "3@0 * 2@0", "2@0 + 3@1"], keys: ["root", "even"] },
};
const PIPE_VALUES = { root: ["A1", "C2", "E1", "G1", "A0", "C1"], max: ["8", "16", "32", "64"] };

/** Calls whose first argument is a name (a uid), not a key. */
const NAME_FIRST = new Set(["viz", "hush", "hold", "gate", "seq", "bang", "use", "level", "xfade", "say", "fx", "group", "style", "solo"]);

/** Calls whose every bare argument is a name: vu(bass, drums, ...). */
const NAME_ALL = new Set(["vu"]);

/**
 * Where the cursor is in `before` (the line up to it): the word being typed,
 * and what kind of thing goes there.
 *   { word, kind: "cue" | "key" | "value" | "name" | "channel", cue, key, used, owner }
 */
export function cursorContext(before) {
  const word = /[\w.]*$/.exec(before)[0];
  const head = before.slice(0, before.length - word.length);
  // the calls we are inside, and where the current argument starts
  const stack = [];                       // { name, argStart, used:Set, objDepth }
  let quote = "", quoteAt = -1;
  for (let i = 0; i < head.length; i++) {
    const ch = head[i];
    if (quote) { if (ch === quote) quote = ""; continue; }
    if (ch === '"' || ch === "'") { quote = ch; quoteAt = i; continue; }
    if (ch === "(") {
      const name = /([A-Za-z_]\w*)\s*$/.exec(head.slice(0, i))?.[1] || "";
      stack.push({ name, at: i, argStart: i + 1, used: new Set(), obj: 0, args: 0 });
    } else if (ch === ")") stack.pop();
    else if (ch === "{" && stack.length) stack[stack.length - 1].obj++;
    else if (ch === "}" && stack.length) stack[stack.length - 1].obj--;
    else if (ch === "," && stack.length) {
      const top = stack[stack.length - 1];
      if (!top.obj) {
        const seg = head.slice(top.argStart, i);
        const k = /^\s*([A-Za-z_]\w*)\s*:/.exec(seg)?.[1];
        if (k) top.used.add(k);
        top.argStart = i + 1;
        top.args++;
      }
    }
  }
  if (quote) {
    // in a quoted value just opened -- cycle:"na| -- a value of that key,
    // all that is typed since the quote (a file name: names.csv, my-lines.txt)
    const top = stack[stack.length - 1];
    const k = top && !top.obj && /^\s*([A-Za-z_]\w*)\s*:\s*$/.exec(head.slice(top.argStart, quoteAt))?.[1];
    if (k) return { word: before.slice(quoteAt + 1), kind: "value", cue: top.name, key: k, quoted: quote };
    return { word, kind: null };
  }
  if (word.includes(".")) return { word, kind: "channel", owner: word.split(".")[0] };
  const top = stack[stack.length - 1];
  if (!top) return { word, kind: "cue" };
  // inside sinOsc( ... ): what its arguments are, from here on
  if (SIGNATURES[top.name] && !top.obj) return { word, kind: "sig", sig: SIGNATURES[top.name].slice(top.args) };
  if (/^(follow|link)$/.test(top.name) && top.args === 0) {
    // follow(/pedal/1 ... -- an incoming OSC address
    const osc = /\/[\w\/.-]*$/.exec(before)?.[0];
    if (osc) return { word: osc, kind: "osc" };
    return { word, kind: "name", inFollow: true };
  }
  // an argument of choose( / Pseq([ ... standing as a parameter's value:
  // a value of that parameter -- wave:choose(square, no|
  if (PICKERS.has(top.name) && !top.obj && stack.length > 1 && /^\s*\[?\s*$/.test(head.slice(top.argStart))) {
    const parent = stack[stack.length - 2];
    const k = /^\s*([A-Za-z_]\w*)\s*:\s*\[?\s*[A-Za-z_]\w*\s*$/.exec(head.slice(parent.argStart, top.at))?.[1];
    if (k && !parent.obj) return { word, kind: "value", cue: parent.name, key: k, inPicker: true };
  }
  const seg = head.slice(top.argStart);
  // freq:Pwhite(40, 100) | -- a pitch filter goes here
  if (!top.obj && /\|\s*$/.test(seg)) return { word, kind: "pipe" };
  // inside one: scale(mi| , scale(minor, ro| , scale(minor, root:| ...
  if (PIPE_FILTERS[top.name] && !top.obj && /\|\s*[A-Za-z_]\w*\s*$/.test(head.slice(0, top.at))) {
    const pk = /^\s*([A-Za-z_]\w*)\s*:\s*$/.exec(seg)?.[1];
    if (pk) return { word, kind: "pipeArg", filter: top.name, key: pk };
    if (/^\s*$/.test(seg)) return { word, kind: "pipeArg", filter: top.name, first: top.args === 0, used: top.used };
    return { word, kind: null };
  }
  if (top.obj) {
    // inside {...}: a key of the object, or one of its values
    const body = seg.slice(seg.lastIndexOf("{") + 1);
    const inner = body.split(",").pop();
    const k = /^\s*([A-Za-z_]\w*)\s*:\s*$/.exec(inner)?.[1];
    if (k) return { word, kind: "value", cue: top.name, key: k, inObject: true };
    // at a key of the object: which object (env:{…), and the keys it has
    const owner = /([A-Za-z_]\w*)\s*:\s*\{[^{]*$/.exec(seg)?.[1];
    if (owner && /^\s*$/.test(inner)) {
      const used = new Set([...body.matchAll(/([A-Za-z_]\w*)\s*:/g)].map(m => m[1]));
      return { word, kind: "key", cue: top.name, inObject: true, owner, used };
    }
    return { word, kind: null };
  }
  const k = /^\s*([A-Za-z_]\w*)\s*:\s*\[?\s*$/.exec(seg)?.[1];
  if (k) return { word, kind: "value", cue: top.name, key: k };
  if (/^\s*$/.test(seg)) {
    if ((NAME_FIRST.has(top.name) && top.args === 0) || NAME_ALL.has(top.name)
      || (top.name === "xfade" && top.args < 2)) return { word, kind: "name", cue: top.name, also: "key" };
    return { word, kind: "key", cue: top.name, used: top.used };
  }
  return { word, kind: null };
}

/** The names a bare first argument can be: use( takes a file from blocks/, vu( the mix or all too, the rest a uid. */
const namesFor = (c, ctx) => (c.cue === "use" ? ctx.blocks
  : c.cue === "vu" ? ["master", "all", ...(ctx.uids || []), "stereo"]
  : c.cue === "fx" ? [...(ctx.buses?.() || []), "room"]
  : c.cue === "style" ? ctx.styles?.() || []
  : c.cue === "solo" ? [...(ctx.uids || []), "off"]
  : c.cue === "level" ? ["all", ...(ctx.uids || []), ...(ctx.buses?.() || [])]
  : ["hold", "gate", "seq", "bang"].includes(c.cue) ? ["all", ...(ctx.uids || [])] : ctx.uids) || [];

/**
 * Suggestions at the cursor: { word, items: [{ label, insert }] }, best
 * first. `insert` is what follows the typed word when it is taken.
 * ctx: { params(cue) -> [{ key, options }], uids: [..], channels: [..] }
 * `before` is the expression up to the cursor -- the lines it carries on
 * from included; `line` the cursor's own line, for the stash's phrases.
 */
export function suggest(before, ctx, line = before) {
  const c = cursorContext(before);
  if (c.kind === "sig") return { word: c.word, kind: "sig", items: [], signature: c.sig.join(", ") };
  if (c.kind === "pipe" || c.kind === "pipeArg") return pipeSuggest(c);
  // at a fresh argument -- rotate(| or rotate(dur:2, | -- nothing typed yet:
  // the common parameters not yet written (the console's reminder)
  const opening = !c.word && (c.kind === "key" || c.kind === "name") && !c.inObject && /[(,]\s*$/.test(before);
  const items = [];
  const add = (label, tail = "") => items.push({ label, tail });
  let choices = false;            // a value with a set of its own (view:, wave:)
  let rest = null;                // a whole set, taken at once: reverb: -> {mix:…, time:…}
  if (c.kind === "cue") {
    for (const n of LIVE_CUES) add(n, "(");
    for (const u of ctx.uids || []) add(u, " > ");      // a target: organ > rotate(...)
  } else if (c.kind === "key" && c.inObject) {
    for (const k of OBJECT_KEYS[c.owner] || []) if (!c.used?.has(k)) add(k, ":");
    // nothing typed yet: the keys not written, with their defaults, all at once (Tab)
    const left = templatePairs(OBJECT_TEMPLATES[c.owner] || "").filter(([k]) => !c.used?.has(k));
    if (!c.word && left.length) rest = left.map(([k, v]) => `${k}:${v}`).join(", ") + "}";
  } else if (c.kind === "key" || c.also === "key") {
    if (c.kind === "name") for (const u of namesFor(c, ctx)) add(u, c.cue === "use" ? "" : ", ");
    const params = paramsOf(c.cue, ctx).filter(p => p?.key && !c.used?.has(p.key));
    if (opening) {
      // just opened the bracket: the common ones, or the cue's own order
      const common = COMMON_PARAMS[c.cue];
      const keys = params.map(p => p.key);
      for (const k of common ? common.filter(k => keys.includes(k)) : keys.filter(k => k !== "uid")) add(k, ":");
    } else for (const p of params) add(p.key, ":");
  } else if (c.kind === "name") {
    for (const u of namesFor(c, ctx)) add(u, c.cue === "use" ? "" : c.cue ? ", " : ".");
    // in follow(: also what is publishing now, and the OSC heard so far
    if (c.inFollow) {
      for (const u of ctx.sources?.() || []) add(u, ".");
      for (const a of ctx.oscAddresses?.() || []) add(a, ", ");
    }
  } else if (c.kind === "osc") {
    for (const a of ctx.oscAddresses?.() || []) add(a, ", ");
  } else if (c.kind === "channel") {
    const typed = c.word.slice(c.word.indexOf(".") + 1);
    // what this name publishes (me: the line's own shape), else the usual ones
    const chs = ctx.channelsOf?.(c.owner, before) || ctx.channels || [];
    for (const ch of chs) items.push({ label: ch, tail: "", typed });
  } else if (c.kind === "value") {
    const p = paramsOf(c.cue, ctx).find(q => q?.key === c.key);
    const asked = c.inObject ? null : askedValues(c, ctx, before);
    const opts = ((c.inObject ? OBJECT_VALUES[c.key] : (p?.options || asked || ANY_CUE_VALUES[c.key])) || [])
      .map(o => String(Array.isArray(o) ? o[0] : o)).filter(Boolean);
    // a name of a file, or a column with spaces in it, goes in quotes: inside
    // the quote opened (cycle:"na|) the rest and the closing one, else whole
    const quoting = !!asked && (c.quoted || (TEXT_SOURCES.has(c.key) || c.key === "col"));
    if (c.quoted) { for (const o of opts) add(o, c.quoted); }
    else if (quoting && c.cue === "text" && TEXT_SOURCES.has(c.key)) { for (const o of opts) add(`"${o}"`); }
    // a column always quoted: col:text would be read as the text() cue
    else if (quoting && c.key === "col") { for (const o of opts) add(`"${o}"`); }
    else for (const o of opts) add(o);
    if (c.quoted) { choices = opts.length > 0; }
    const whole = !c.quoted && !c.inObject && !c.inPicker && OBJECT_TEMPLATES[c.key];
    if (whole) add(whole);                       // env: -> the whole {a:…, d:…, s:…, r:…}
    if (whole && !c.word) rest = whole;          // ... taken whole with Tab
    else if (!c.inPicker && !c.quoted) for (const f of VALUE_FUNCS) add(f, "(");
    if (!c.quoted) choices = opts.length > 0 || !!whole;
  }
  // what is typed so far of the thing being completed
  const typed = c.kind === "channel" ? c.word.slice(c.word.indexOf(".") + 1) : c.word;
  // Those that begin with it first, then those that have it anywhere (lar:
  // polar) -- for a value whose first letter you do not know. Taking one of
  // those replaces what is typed (`replace` letters before the caret).
  const seen = new Set();
  const out = [];
  for (const it of items) {
    if (seen.has(it.label) || !it.label.startsWith(typed) || it.label === typed && !it.tail) continue;
    seen.add(it.label);
    out.push({ label: it.label, insert: it.label.slice(typed.length) + it.tail });
  }
  if (typed) {
    const low = typed.toLowerCase();
    // the start of one in another case (envper): the rest is the ghost, and
    // taking it puts the letters typed right (recase: envPer)
    for (const it of items) {
      if (seen.has(it.label) || !it.label.toLowerCase().startsWith(low)) continue;
      seen.add(it.label);
      out.push({ label: it.label, insert: it.label.slice(typed.length) + it.tail, recase: it.label.slice(0, typed.length) });
    }
    for (const it of items) {
      if (seen.has(it.label) || !it.label.toLowerCase().includes(low)) continue;
      seen.add(it.label);
      out.push({ label: it.label, insert: it.label + it.tail, replace: typed.length });
    }
  }
  // a whole name that begins others too (env, envPerStep): the ghost can only
  // offer one -- the console lists them all
  const ambiguous = !!typed && out.some(i => i.label === typed)
    && out.some(i => i.label !== typed && i.label.startsWith(typed));
  // the stash's phrases are whole lines: matched against the caret's line
  // only, as taking one replaces what is typed on it
  return { word: typed, kind: c.kind, items: stashed(line, ctx.phrases, out), choices, ambiguous, opening, rest };
}

/** After | : the filters; inside one: its first argument, its keys, their values -- and its reminder. */
function pipeSuggest(c) {
  const f = PIPE_FILTERS[c.filter];
  let labels = [], tail = "";
  if (c.kind === "pipe") { labels = Object.keys(PIPE_FILTERS); tail = "("; }
  else if (c.key) labels = PIPE_VALUES[c.key] || [];
  else {
    labels = [...(c.first ? f.first : []), ...f.keys.filter(k => !c.used?.has(k)).map(k => (k === "even" ? k : k + ":"))];
  }
  const typed = c.word, low = typed.toLowerCase();
  const items = labels.filter(l => l.toLowerCase().startsWith(low) && l !== typed)
    .map(l => ({ label: l.replace(/:$/, ""), insert: l.slice(typed.length) + tail }));
  const signature = c.kind === "pipe" ? Object.keys(PIPE_FILTERS).map(n => n + "(").join("  ") : `${c.filter}(${f.sig})`;
  return { word: typed, kind: c.kind, items, choices: items.length > 0, ambiguous: false, opening: false, rest: null, signature };
}

/**
 * The stash's phrases among the suggestions. A phrase is matched against
 * the whole line typed so far, not the word: one that begins with it comes
 * first, so the ghost offers the rest of it; one that has it anywhere --
 * `drone` in synth(uid:drone, ...) -- only joins the end of the list, and
 * taking it replaces what is typed. A phrase of several lines shows its
 * first, and comes back whole.
 */
function stashed(before, phrases, items) {
  const line = before.trimStart();
  if (!line || !phrases?.length) return items;
  const label = p => {
    const first = p.split("\n")[0];
    return first.length > 60 ? first.slice(0, 60) + " …" : first + (first === p ? "" : " …");
  };
  const starts = [], within = [];
  const low = line.toLowerCase();
  for (const p of phrases) {
    if (p === line) continue;
    if (p.startsWith(line)) starts.push({ label: label(p), insert: p.slice(line.length), phrase: true });
    else if (line.length > 1 && p.toLowerCase().includes(low)) {
      within.push({ label: label(p), insert: p, replace: line.length, phrase: true });
    }
  }
  return [...starts, ...items, ...within];
}
